/**
 * Tile model.
 *
 * Notation: `<rank><suit>` — compact, unambiguous, and easy for both the
 * vision layer to emit and a human to eyeball in a correction screen.
 *
 *   m = characters / wan / 萬   1m..9m
 *   p = dots / tong / 筒        1p..9p
 *   s = bamboo / suo / 索       1s..9s
 *   z = honours                 1z=East 2z=South 3z=West 4z=North
 *                               5z=White(board) 6z=Green(fa) 7z=Red(zhong)
 *
 * Flowers and seasons are NOT tiles (`Tile`/`isTile`) — they never form
 * melds and never count toward the 14-tile hand. They're tracked as their
 * own small notation instead (`BonusTile`/`isBonusTile`) — F1-F4 for the
 * four flowers, S1-S4 for the four seasons — since which specific one a
 * player holds matters for the seat/round-matching bonus, not just how many.
 */

export type Suit = 'm' | 'p' | 's' | 'z';
export type Tile = string; // validated at runtime by isTile()

export const WINDS = ['1z', '2z', '3z', '4z'] as const;
export const DRAGONS = ['5z', '6z', '7z'] as const;

export type Wind = 'east' | 'south' | 'west' | 'north';

export const WIND_TILE: Record<Wind, Tile> = {
  east: '1z',
  south: '2z',
  west: '3z',
  north: '4z',
};

const TILE_RE = /^([1-9])([mps])$|^([1-7])(z)$/;

export function isTile(t: string): t is Tile {
  return TILE_RE.test(t);
}

export function rankOf(t: Tile): number {
  return Number(t[0]);
}

export function suitOf(t: Tile): Suit {
  return t[1] as Suit;
}

export function isHonour(t: Tile): boolean {
  return suitOf(t) === 'z';
}

export function isWind(t: Tile): boolean {
  return suitOf(t) === 'z' && rankOf(t) <= 4;
}

export function isDragon(t: Tile): boolean {
  return suitOf(t) === 'z' && rankOf(t) >= 5;
}

/** Terminal = 1 or 9 of a numbered suit. */
export function isTerminal(t: Tile): boolean {
  return !isHonour(t) && (rankOf(t) === 1 || rankOf(t) === 9);
}

/** Terminal or honour — the "yao jiu" set used by several limit hands. */
export function isTerminalOrHonour(t: Tile): boolean {
  return isHonour(t) || isTerminal(t);
}

/** Simple = 2..8 of a numbered suit. */
export function isSimple(t: Tile): boolean {
  return !isTerminalOrHonour(t);
}

/**
 * Parse a compact hand string into tiles.
 * Accepts both "1m2m3m" and the shorthand "123m" / "123m456p 77z".
 */
export function parseTiles(input: string): Tile[] {
  const out: Tile[] = [];
  let pending: string[] = [];

  for (const ch of input.replace(/[\s,]/g, '')) {
    if (ch >= '0' && ch <= '9') {
      pending.push(ch);
    } else if (ch === 'm' || ch === 'p' || ch === 's' || ch === 'z') {
      if (pending.length === 0) {
        throw new Error(`Suit "${ch}" with no preceding rank in "${input}"`);
      }
      for (const r of pending) {
        const tile = `${r}${ch}`;
        if (!isTile(tile)) throw new Error(`Invalid tile "${tile}" in "${input}"`);
        out.push(tile);
      }
      pending = [];
    } else {
      throw new Error(`Unexpected character "${ch}" in "${input}"`);
    }
  }

  if (pending.length > 0) {
    throw new Error(`Trailing ranks with no suit in "${input}"`);
  }
  return out;
}

/** Render tiles back to the compact shorthand, grouped by suit. */
export function formatTiles(tiles: Tile[]): string {
  const order: Suit[] = ['m', 'p', 's', 'z'];
  const groups = new Map<Suit, number[]>();
  for (const t of sortTiles(tiles)) {
    const s = suitOf(t);
    if (!groups.has(s)) groups.set(s, []);
    groups.get(s)!.push(rankOf(t));
  }
  return order
    .filter((s) => groups.has(s))
    .map((s) => groups.get(s)!.join('') + s)
    .join('');
}

const SUIT_ORDER: Record<Suit, number> = { m: 0, p: 1, s: 2, z: 3 };

export function sortTiles(tiles: Tile[]): Tile[] {
  return [...tiles].sort(
    (a, b) => SUIT_ORDER[suitOf(a)] - SUIT_ORDER[suitOf(b)] || rankOf(a) - rankOf(b),
  );
}

/** Multiset counter keyed by tile. */
export function countTiles(tiles: Tile[]): Map<Tile, number> {
  const m = new Map<Tile, number>();
  for (const t of tiles) m.set(t, (m.get(t) ?? 0) + 1);
  return m;
}

/**
 * Structural validity of the tile pool itself, independent of hand shape.
 * A standard set has exactly 4 of each tile.
 */
export function validateTilePool(tiles: Tile[]): string[] {
  const problems: string[] = [];
  for (const t of tiles) {
    if (!isTile(t)) problems.push(`"${t}" is not a valid tile`);
  }
  for (const [tile, n] of countTiles(tiles)) {
    if (n > 4) problems.push(`${n}× ${tile} — only 4 exist in a set`);
  }
  return problems;
}

// ---------------------------------------------------------------------------
// Flowers & seasons — F1-F4, S1-S4. Never part of `Tile`/the 14-tile hand.
// ---------------------------------------------------------------------------

export type BonusKind = 'flower' | 'season';
export type BonusTile = string; // validated at runtime by isBonusTile()

const BONUS_TILE_RE = /^([FS])([1-4])$/;

export function isBonusTile(t: string): t is BonusTile {
  return BONUS_TILE_RE.test(t);
}

export function bonusKindOf(t: BonusTile): BonusKind {
  return t[0] === 'F' ? 'flower' : 'season';
}

export function bonusNumberOf(t: BonusTile): number {
  return Number(t[1]);
}

/** Which wind a flower/season's printed NUMBER corresponds to: 1=East, 2=South, 3=West, 4=North. */
const NUMBER_WIND: Record<number, Wind> = { 1: 'east', 2: 'south', 3: 'west', 4: 'north' };

export function bonusWindOf(t: BonusTile): Wind {
  return NUMBER_WIND[bonusNumberOf(t)]!;
}

/** Parse space/comma-separated bonus-tile notation, e.g. "F1 S3". */
export function parseBonusTiles(input: string): BonusTile[] {
  const out: BonusTile[] = [];
  for (const raw of input.split(/[\s,]+/).filter(Boolean)) {
    const t = raw.toUpperCase();
    if (!isBonusTile(t)) {
      throw new Error(`Invalid flower/season "${raw}" — expected F1-F4 (flowers) or S1-S4 (seasons)`);
    }
    out.push(t);
  }
  return out;
}

export function formatBonusTiles(tiles: BonusTile[]): string {
  return [...tiles].sort().join(' ');
}
